import { useMutation, useSuspenseQuery } from "@apollo/client";
import { capitaliseFirstLetter, capitaliseFirstLetterOfEachWord } from "@shared/translations/src";
import { getRouteApi } from "@tanstack/react-router";
import { Pen } from "lucide-react";
import { Suspense, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { type z } from "zod";
import { DocumentTileError, DocumentTileSkeleton } from "@/components/DocumentTile";
import { ErrorState } from "@/components/ErrorState";
import { InfoField } from "@/components/InfoField";
import { Heading1 } from "@/components/Typography";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactDocuments } from "@/contacts/ContactDocuments";
import { ContactForm } from "@/contacts/ContactForm";
import { type contactSchema } from "@/contacts/ContactForm/validation";
import { graphql, type VariablesOf, type ResultOf } from "@/graphql";
import { useToast } from "@/hooks/use-toast";
import { removeNothings } from "@/lib/utils/arrays";
import { cn, nullable } from "@/lib/utils/common";
import { extractErrorMessages, getErrorMessage } from "@/lib/utils/errors";
import {
  formatAddress,
  formatEmptyableValue,
  formatEnumLabel,
  formatInteger,
  formatName,
  formatPhoneNumber,
} from "@/lib/utils/formatting";
import { findStateCode } from "@/lib/utils/states";
import { transformForApi } from "@/lib/utils/transforms";

const routeApi = getRouteApi("/(authed)/_layout/contacts/$contactId");

export function Contact({ contactName }: { contactName: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const { contactId } = routeApi.useParams();

  return (
    <>
      <ContactTitle title={contactName} onEdit={() => setIsOpen(true)} />
      <Card className="m-4 p-4 bg-muted-light">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <ErrorBoundary
            FallbackComponent={({ error }) => <ErrorState message={getErrorMessage(error)} className="col-span-full" />}
          >
            <Suspense fallback={<ContactSkeleton />}>
              <ContactDetails contactId={contactId} setIsOpen={setIsOpen} isOpen={isOpen} />
            </Suspense>
          </ErrorBoundary>
        </div>
      </Card>
      <hr className="border-border" />
      <div className="bg-muted p-4 h-full">
        <ErrorBoundary FallbackComponent={DocumentTileError}>
          <Suspense fallback={<DocumentTileSkeleton />}>
            <ContactDocuments contactId={contactId} />
          </Suspense>
        </ErrorBoundary>
      </div>
    </>
  );
}

function ContactTitle({ title, onEdit }: { title: string; onEdit: () => void }) {
  return (
    <div className="flex justify-between h-16">
      <Heading1 className="truncate px-4 py-2" title={title}>
        {title}
      </Heading1>
      <Button className="m-4" variant="outline" onClick={onEdit}>
        <Pen className="h-4 w-4" />
        Edit
      </Button>
    </div>
  );
}

function ContactDetails({
  contactId,
  isOpen,
  setIsOpen,
}: {
  contactId: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { data } = useSuspenseQuery(supplyChainPartnerDetailsQuery, { variables: { id: contactId } });
  const [updateSupplyChainPartner] = useMutation(updateSupplyChainPartnerMutation);
  const [archiveSupplyChainPartners, { loading: isDeleting }] = useMutation(archiveSupplyChainPartnersMutation);
  const navigate = routeApi.useNavigate();
  const { toast } = useToast();

  const onSubmit = async (values: z.infer<typeof contactSchema>) => {
    try {
      const result = await updateSupplyChainPartner({
        variables: {
          input: {
            id: contactId,
            businessName: transformForApi.text(values.businessName),
            role: values.role,
            type: values.type,
            addressLine1: transformForApi.text(values.addressLine1),
            addressLine2: nullable(transformForApi.text(values.addressLine2)),
            state: values.state,
            city: transformForApi.text(values.city),
            postcode: transformForApi.text(values.postCode),
            country: values.countryId,
            contactEmail: nullable(transformForApi.email(values.contactEmail)),
            contactPhone: values.contactPhone,
            managedBy: nullable(values.businessAccountManagerId),
            contactFirstName: nullable(transformForApi.text(values.contactFirstName)),
            contactLastName: nullable(transformForApi.text(values.contactLastName)),
            accountNumber: nullable(transformForApi.identifier(values.accountNumber)),
            monthlyTarget: !!values.monthlyTarget ? Number(values.monthlyTarget) : null,
            note: nullable(values.note),
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error updating ${values.businessName}`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      setIsOpen(false);

      toast({
        title: "Success",
        description: `Contact ${values.businessName} updated successfully`,
        variant: "default",
      });
    } catch (error) {
      toast({
        title: `Error updating ${values.businessName}`,
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };
  const onDelete = async () => {
    try {
      const input: VariablesOf<typeof archiveSupplyChainPartnersMutation>["input"] = {
        supplyChainPartners: contactId ? [contactId] : [],
      };

      if (input.supplyChainPartners.length < 1) {
        toast({
          title: "Failed to delete contact",
          description: "No contact selected",
          variant: "destructive",
        });

        return;
      }

      const result = await archiveSupplyChainPartners({
        variables: {
          input,
        },
      });

      const failedPartners = result.data?.archiveSupplyChainPartners?.couldNotArchive || [];

      if (failedPartners.length > 0) {
        const reasons = removeNothings(failedPartners.map((partner) => partner?.reason));
        toast({
          title: "Error deleting contact",
          description: reasons.join(", "),
          variant: "destructive",
        });

        return;
      }

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: `Error deleting contact`,
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      setIsOpen(false);

      toast({
        title: "Success",
        description: "Contact successfully deleted",
        variant: "default",
      });

      await navigate({ to: "/contacts", replace: true });
    } catch (error) {
      toast({
        title: "Error deleting contact",
        description: getErrorMessage(error),
        variant: "destructive",
      });
    }
  };

  const onCancel = () => {
    setIsOpen(false);
  };

  const contact = data.supplyChainPartner;
  const primaryContact = contact?.primaryContact;

  const contactInfo = [
    {
      label: "Business Name",
      value: formatEmptyableValue(contact?.businessName),
      fullWidth: false,
    },
    {
      label: "Account Number",
      value: capitaliseFirstLetter(formatEmptyableValue(contact?.accountNumber)),
      fullWidth: false,
    },
    {
      label: "Role",
      value: capitaliseFirstLetterOfEachWord(formatEnumLabel(contact?.role || "\u2014")),
      fullWidth: false,
    },
    {
      label: "Type",
      value: capitaliseFirstLetter(formatEmptyableValue(contact?.type?.label)),
      fullWidth: false,
    },
    {
      label: "Principal Contact Name",
      value: formatName({ firstName: primaryContact?.firstName, lastName: primaryContact?.lastName }),
      fullWidth: false,
    },
    {
      label: "Principal Contact Email",
      value: formatEmptyableValue(primaryContact?.email),
      fullWidth: false,
    },
    {
      label: "Principal Contact Phone",
      value: formatPhoneNumber(primaryContact?.phone),
      fullWidth: false,
    },
    {
      label: "Business Account Manager",
      value: formatName({ firstName: contact?.managedBy?.firstName, lastName: contact?.managedBy?.lastName }),
      fullWidth: false,
    },
    {
      label: "Business Manager Email",
      value: formatEmptyableValue(contact?.managedBy?.email),
      fullWidth: false,
    },
    {
      label: "Monthly Target",
      value: formatInteger(contact?.monthlyTarget),
      fullWidth: false,
    },
    {
      label: "Address",
      value: formatAddress(contact?.businessAddress),
      fullWidth: true,
    },
    {
      label: "Note",
      value: contact?.note ? <div className="whitespace-pre-wrap break-words">{contact.note}</div> : "—",
      fullWidth: true,
    },
  ] as const;

  return (
    <>
      <article className="contents" data-testid="contact-details">
        {contactInfo.map(({ label, value, fullWidth }) => (
          <InfoField key={label} fullWidth={fullWidth}>
            <CardDescription className="text-foreground/80">{label}</CardDescription>
            <CardTitle
              className={cn("leading-relaxed", typeof value === "string" && "truncate")}
              title={typeof value === "string" ? value : undefined}
            >
              {value}
            </CardTitle>
          </InfoField>
        ))}
      </article>

      <AlertDialog
        open={isOpen}
        onOpenChange={(open) => {
          if (open) {
          }
          setIsOpen(open);
        }}
      >
        <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Edit Contact</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Edit Contact Form</AlertDialogDescription>
          </AlertDialogHeader>
          <ContactForm
            onSubmit={onSubmit}
            onCancel={onCancel}
            onDelete={onDelete}
            isDeleting={isDeleting}
            initialData={transformContactToFormData(contact)}
          />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ContactSkeleton() {
  const skeletonInfo = [...Array(12).keys()];

  return (
    <>
      {skeletonInfo.map((num) => (
        <InfoField key={num} fullWidth={num === 10 || num === 11}>
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-5 w-40" />
        </InfoField>
      ))}
    </>
  );
}

export const transformContactToFormData = (
  contact: ResultOf<typeof supplyChainPartnerDetailsQuery>["supplyChainPartner"] | undefined,
): z.infer<typeof contactSchema> | undefined => {
  if (!contact) return undefined;

  const primaryContact = contact.primaryContact;

  return {
    businessName: contact.businessName,
    type: contact.type?.id || "",
    accountNumber: contact.accountNumber || "",
    role: contact.role,
    contactFirstName: primaryContact?.firstName || "",
    contactLastName: primaryContact?.lastName || "",
    contactEmail: primaryContact?.email || "",
    contactPhone: primaryContact?.phone || "",
    businessAccountManagerId: contact?.managedBy?.id || "",
    addressLine1: contact.businessAddress?.line1 || "",
    addressLine2: contact.businessAddress?.line2 || "",
    city: contact.businessAddress?.city || "",
    state: findStateCode(contact.businessAddress?.state),
    postCode: contact.businessAddress?.postcode || "",
    countryId: "12",
    monthlyTarget: contact.monthlyTarget || undefined,
    note: contact.note || "",
  };
};

export const supplyChainPartnerDetailsQuery = graphql(`
  query SupplyChainPartnerDetails($id: UUID!) {
    supplyChainPartner(id: $id) {
      id
      businessName
      role
      accountNumber
      type {
        id
        label
      }
      businessAddress {
        id
        line1
        line2
        city
        state
        postcode
      }
      managedBy {
        id
        firstName
        lastName
        email
      }
      primaryContact {
        id
        firstName
        lastName
        email
        phone
      }
      monthlyTarget
      note
    }
  }
`);

export const archiveSupplyChainPartnersMutation = graphql(`
  mutation ArchiveSupplyChainPartners($input: ArchiveSupplyChainPartnersInput!) {
    archiveSupplyChainPartners(input: $input) {
      couldNotArchive {
        id
        reason
      }
      supplyChainPartners {
        id
      }
    }
  }
`);

export const updateSupplyChainPartnerMutation = graphql(`
  mutation UpdateSupplyChainPartner($input: UpdateSupplyChainPartnerInput!) {
    updateSupplyChainPartner(input: $input) {
      supplyChainPartner {
        id
        accountNumber
        monthlyTarget
        businessAddress {
          id
          line1
          line2
          city
          postcode
          state
          country {
            id
            name
          }
        }
        primaryContact {
          id
          firstName
          lastName
          email
          phone
        }
        businessName
        role
        status
        managedBy {
          id
          firstName
          lastName
          email
        }
        type {
          id
          label
        }
        note
      }
    }
  }
`);
